import { ApiError } from '@repo/api';
import { agentManageApi } from '@repo/api/clients/agentManage.api';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { type AgentOwner, type CriticalityLevel } from '../component';
import { FILTER_OPTIONS_LIMIT, LIMIT_CALL } from '../constants/managepage';
import { agentManageKeys } from '../data-sources/queryKeys';

/**
 * Fetches a paginated, filterable, and sortable list of AI agents.
 *
 * Uses `useInfiniteQuery` with manual fetching (`enabled: false`). Pages are
 * 1-indexed; the next page is derived from the API's `hasNextPage` flag.
 *
 * @param searchQuery - Free-text search string applied server-side.
 * @param filters - Active filter selections keyed by filter category.
 * @param sort - Column sort descriptor (`field` + `direction`).
 * @param limit - Page size; defaults to `FILTER_OPTIONS_LIMIT`.
 * @returns The standard `useInfiniteQuery` result whose pages contain `{ agents, pagination }`.
 */
export const useAgentList = (
  searchQuery: string,
  filters: Record<string, string[]>,
  sort: { field: string; direction: 'asc' | 'desc' },
  limit: number = LIMIT_CALL
) => {
  return useInfiniteQuery({
    queryKey: agentManageKeys.agentsList(searchQuery, filters, sort, limit),
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const payload = {
        searchQuery,
        filters,
        sort,
        pagination: {
          page: pageParam,
          limit,
        },
      };

      const { data, pagination } = await agentManageApi.getAgentsList(payload);

      return {
        agents: data,
        pagination: {
          ...pagination,
          currentPage: pageParam,
        },
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage
        ? lastPage.pagination.currentPage + 1
        : undefined;
    },
    enabled: false, // 👈 prevents auto fetch
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

/**
 * Fetches paginated filter-option values for a given filter category.
 *
 * Uses `useInfiniteQuery` with manual fetching (`enabled: false`). Pages are
 * 1-indexed; the next page is derived from the API's `hasNextPage` flag.
 *
 * @param target - The filter category to retrieve options for (e.g. "status", "owner").
 * @param searchQuery - Optional search string to narrow the returned options.
 * @param limit - Page size; defaults to `LIMIT_CALL`.
 * @returns The standard `useInfiniteQuery` result whose pages contain `{ options, pagination }`.
 */
export const useFilterOptions = (
  target: string,
  searchQuery: string,
  limit: number = FILTER_OPTIONS_LIMIT
) => {
  return useInfiniteQuery({
    queryKey: agentManageKeys.filterOptions(target, searchQuery, limit),
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const payload = {
        target,
        searchQuery,
        pagination: {
          page: pageParam,
          limit,
        },
      };

      const { options, pagination } =
        await agentManageApi.getFilterOptions(payload);

      return {
        options,
        pagination: {
          ...pagination,
          currentPage: pageParam, // Add the page number since API doesn't return it
        },
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage
        ? lastPage.pagination.currentPage + 1
        : undefined;
    },
    enabled: false,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

/**
 * Fetches paginated label keys for the label-selection dropdown.
 *
 * Uses `useInfiniteQuery` with manual fetching (`enabled: false`). Pages are
 * 0-indexed; the offset sent to the API is computed as `pageParam * max`.
 *
 * @param searchText - Text used to filter label keys server-side.
 * @param max - Maximum number of labels to return per page.
 * @returns An object containing:
 *  - `data` – the infinite query data (pages of `{ data, pagination }`).
 *  - `isLoading` – whether a fetch is currently in flight.
 *  - `isError` / `error` – error state from the query.
 *  - `fetchFn` – triggers a manual refetch (`refetch`).
 *  - `fetchNextPage` – loads the next page of results.
 */
export const useGetLabels = (searchText: string, max: number = LIMIT_CALL) => {
  return useInfiniteQuery({
    queryKey: agentManageKeys.labels(searchText, max),
    queryFn: async ({ pageParam = 0 }: { pageParam?: number }) => {
      const offset = pageParam * max;

      const payload = {
        filter: searchText,
        offset,
        max,
      };

      const { data, pagination } = await agentManageApi.getLabels(payload);

      return {
        data,
        pagination: {
          ...pagination,
          currentPage: pageParam,
        },
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage
        ? lastPage.pagination.currentPage + 1
        : undefined;
    },
    enabled: false,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

interface UseSaveLabelProps {
  onSuccess: (labels: Record<string, string>) => void;
  onError: (error: string) => void;
}

/**
 * Saves labels for a given agent via a PUT request.
 *
 * @param options.onSuccess - Callback invoked after a successful save.
 * @param options.onError - Callback invoked when the save fails.
 * @returns A `useMutation` result; call `mutate({ agentId, labels })` to trigger.
 */
export const useSaveLabels = ({ onSuccess, onError }: UseSaveLabelProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      labels,
    }: {
      agentId: string;
      labels: Record<string, string>;
    }) =>
      agentManageApi.saveLabels({
        agentId,
        labels,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...agentManageKeys.all, 'labels'],
      });

      onSuccess(variables.labels);
    },
    onError: (error) => {
      if (
        error instanceof ApiError &&
        typeof error.data === 'object' &&
        error.data !== null &&
        'error' in error.data
      ) {
        const errorMessage = (error.data as { error: unknown }).error;

        if (typeof errorMessage === 'string' && errorMessage.length > 0) {
          onError(errorMessage);
          return;
        }
      }

      onError(
        intl.formatMessage({
          id: 'dmk46A',
          defaultMessage:
            'Unable to update labels at the moment. Please retry.',
          description: 'Error message for label update failure',
        })
      );
    },
  });
};

interface UseSaveOwnersProps {
  onSuccess: (response: AgentOwner[]) => void;
  onError: (error: string) => void;
}

/**
 * Saves owners for a given agent via a PUT request.
 *
 * @param options.onSuccess - Callback invoked after a successful save.
 * @param options.onError - Callback invoked when the save fails.
 * @returns A `useMutation` result; call `mutate({ agentId, owners })` to trigger.
 */
export const useSaveOwners = ({ onSuccess, onError }: UseSaveOwnersProps) => {
  const intl = useIntl();

  return useMutation({
    mutationFn: async ({
      agentId,
      owners,
    }: {
      agentId: string;
      owners: AgentOwner[];
    }) =>
      agentManageApi.saveOwners({
        agentId,
        owners: owners.map((o) => ({
          name: o.name,
          email: o.email,
          role: o.role,
          rank: o.rank,
          source: o.source ?? 'Manual',
        })),
      }),
    onSuccess: (_, variables) => onSuccess(variables.owners),
    onError: (error) => {
      if (
        error instanceof ApiError &&
        typeof error.data === 'object' &&
        error.data !== null &&
        'error' in error.data
      ) {
        const errorMessage = (error.data as { error: unknown }).error;

        if (typeof errorMessage === 'string' && errorMessage.length > 0) {
          onError(errorMessage);
          return;
        }
      }

      onError(
        intl.formatMessage({
          id: 'ELDrlW',
          defaultMessage:
            'Unable to update owners at the moment. Please retry.',
          description: 'Error message for owner update failure',
        })
      );
    },
  });
};

/**
 * Fetches the full identity list (users/groups) for the owner-assignment picker.
 *
 * Uses `useQuery` with a 2-minute stale time. The search is performed server-side
 * via {@link agentManageApi.getAgentIdentities}.
 *
 * @param params.searchText - Free-text search applied to identity name/email (defaults to `""`).
 * @param params.pageSize - Maximum identities to return (defaults to `3000`).
 * @param params.enabled - Whether the query should run automatically (defaults to `true`).
 * @returns The standard `useQuery` result whose `data` is an array of identity objects.
 */
export function useAgentIdentity(params?: {
  searchText?: string;
  pageSize?: number;
  enabled?: boolean;
}) {
  const { searchText = '', pageSize = 3000, enabled = true } = params ?? {};

  return useQuery({
    queryKey: agentManageKeys.agentIdentityList({ searchText }),
    queryFn: async () => {
      const options = await agentManageApi.getAgentIdentities({
        pageSize,
        searchText: searchText.trim(),
        filters: [],
      });

      return options;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
}

interface UseSaveAgentProps {
  onSuccess: (criticality: CriticalityLevel) => void;
  onError: (error: string) => void;
}

/**
 * Saves agent-level fields (currently criticality) via a PUT request.
 *
 * @param options.onSuccess - Callback invoked after a successful save.
 * @param options.onError - Callback invoked with a localised error message when the save fails.
 * @returns A `useMutation` result; call `mutate({ agentId, criticality })` to trigger.
 */
export const useSaveAgent = ({ onSuccess, onError }: UseSaveAgentProps) => {
  const intl = useIntl();

  return useMutation({
    mutationFn: async ({
      agentId,
      criticality,
    }: {
      agentId: string;
      criticality: CriticalityLevel;
    }) =>
      agentManageApi.saveAgent({
        agentId,
        criticality,
        criticalityOverride: true,
      }),
    onSuccess: (_, variables) => onSuccess(variables.criticality),
    onError: (error) => {
      if (
        error instanceof ApiError &&
        typeof error.data === 'object' &&
        error.data !== null &&
        'error' in error.data
      ) {
        const errorMessage = (error.data as { error: unknown }).error;

        if (typeof errorMessage === 'string' && errorMessage.length > 0) {
          onError(errorMessage);
          return;
        }
      }

      onError(
        intl.formatMessage({
          id: 'e3+wvz',
          defaultMessage:
            'Unable to update criticality at the moment. Please retry.',
          description: 'Error message for criticality update failure',
        })
      );
    },
  });
};
